import { Component, OnInit, ViewChild } from '@angular/core';
import {
  ModalController,
  AlertController,
  ToastController,
  LoadingController
} from '@ionic/angular';
import { NewAdvertisingComponent } from './new-advertising/new-advertising.component';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Logger } from '../core/logger.service';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { IonReorderGroup } from '@ionic/angular';

const log = new Logger('Advertising');

@Component({
  selector: 'app-advertising',
  templateUrl: './advertising.component.html',
  styleUrls: ['./advertising.component.scss']
})
export class AdvertisingComponent implements OnInit {

  @ViewChild(IonReorderGroup)
  reorderGroup!: IonReorderGroup;

  /**
   * Todas las publicidades cargadas.
   *
   * Incluye activas e inactivas.
   */
  advertisings: any[] = [];

  /**
   * Lista que realmente se muestra.
   *
   * Esta lista es la que utilizamos para búsqueda
   * y reordenamiento.
   */
  displayedAdvertisings: any[] = [];

  /**
   * Estado actual.
   */
  selectedStatus: 'active' | 'inactive' = 'active';

  /**
   * Buscador.
   */
  search: string = '';

  /**
   * Modo reordenamiento.
   */
  canReOrder: boolean = false;

  /**
   * Indica si se están cargando datos.
   */
  loading: boolean = false;

  /**
   * Colecciones.
   */
  private readonly ACTIVE_COLLECTION = 'advertising';

  private readonly INACTIVE_COLLECTION = 'advertising_disabled';

  constructor(
    private modalC: ModalController,
    private firestore: AngularFirestore,
    private alertC: AlertController,
    private toastC: ToastController,
    private aStorage: AngularFireStorage,
    private loadingCtrl: LoadingController
  ) {}

  ngOnInit(): void {
    this.initializeApp();
  }

  /**
   * Carga ambas colecciones.
   */
  async initializeApp(): Promise<void> {

    try {

      this.loading = true;

      /**
       * Obtener activas.
       */
      const activeSnapshot = await this.firestore
        .collection(this.ACTIVE_COLLECTION)
        .ref
        .get();

      /**
       * Obtener inactivas.
       */
      const inactiveSnapshot = await this.firestore
        .collection(this.INACTIVE_COLLECTION)
        .ref
        .get();

      /**
       * Mapear activas.
       */
      const active = activeSnapshot.docs.map(doc => {

        const data: any = doc.data();

        return {
          ...data,
          id: doc.id,
          _status: 'active'
        };

      });

      /**
       * Mapear inactivas.
       */
      const inactive = inactiveSnapshot.docs.map(doc => {

        const data: any = doc.data();

        return {
          ...data,
          id: doc.id,
          _status: 'inactive'
        };

      });

      /**
       * Unificar.
       */
      this.advertisings = [
        ...active,
        ...inactive
      ];

      /**
       * Orden inicial.
       */
      this.sortAdvertisings();

      /**
       * Aplicar filtro/búsqueda.
       */
      this.applyFilters();

    } catch (error) {

      log.error(error);

      await this.showToast(
        'No se pudieron cargar las publicidades',
        'danger'
      );

    } finally {

      this.loading = false;
    }
  }

  /**
   * Ordena las publicidades por estado y order.
   *
   * Cada colección mantiene su propio orden.
   */
  private sortAdvertisings(): void {

    this.advertisings.sort((a, b) => {

      /**
       * Primero comparamos el estado.
       */
      if (a._status !== b._status) {

        return a._status === this.selectedStatus
          ? -1
          : 1;
      }

      /**
       * Después el order.
       */
      const orderA =
        typeof a.order === 'number'
          ? a.order
          : Number.MAX_SAFE_INTEGER;

      const orderB =
        typeof b.order === 'number'
          ? b.order
          : Number.MAX_SAFE_INTEGER;

      return orderA - orderB;
    });
  }

  /**
   * Aplica:
   *
   * 1. Estado
   * 2. Búsqueda
   */
  applyFilters(): void {

    let result = this.advertisings.filter(
      advertising =>
        advertising._status === this.selectedStatus
    );

    const search = this.search
      ?.trim()
      .toLowerCase();

    /**
     * Si no hay búsqueda,
     * mostramos todo el estado actual.
     */
    if (!search) {

      this.displayedAdvertisings = [...result];

      return;
    }

    /**
     * Búsqueda local.
     */
    result = result.filter(advertising => {

      const content =
        advertising?.content
          ?.toString()
          ?.toLowerCase() || '';

      /**
       * Tags.
       */
      const tags = Array.isArray(advertising?.theTags)
        ? advertising.theTags
            .map((tag: any) =>
              tag?.name
                ?.toString()
                ?.toLowerCase() || ''
            )
            .join(' ')
        : '';

      /**
       * También revisamos otros campos comunes
       * por si existen.
       */
      const title =
        advertising?.title
          ?.toString()
          ?.toLowerCase() || '';

      const description =
        advertising?.description
          ?.toString()
          ?.toLowerCase() || '';

      const searchableText = [
        content,
        tags,
        title,
        description
      ].join(' ');

      return searchableText.includes(search);
    });

    this.displayedAdvertisings = result;
  }

  /**
   * Búsqueda.
   *
   * Ahora es completamente local.
   */
  doSearch(): void {

    this.applyFilters();
  }

  /**
   * Búsqueda en tiempo real.
   */
  onSearch(event: any): void {

    this.search =
      event?.detail?.value || '';

    this.applyFilters();
  }

  /**
   * Limpiar búsqueda.
   */
  clearSearch(): void {

    this.search = '';

    this.applyFilters();
  }

  /**
   * Cambiar entre activas e inactivas.
   */
  changeStatus(): void {

    /**
     * Desactivamos reorder al cambiar
     * de sección.
     */
    this.canReOrder = false;

    this.applyFilters();
  }

  /**
   * Cambia el modo de ordenamiento.
   */
  toggleReorder(): void {

    /**
     * No permitir reordenar mientras
     * existe una búsqueda.
     *
     * Esto evita guardar un orden incorrecto
     * cuando solamente estamos viendo una parte
     * de la colección.
     */
    if (this.search.trim()) {

      this.showToast(
        'Limpia la búsqueda antes de reordenar',
        'warning'
      );

      return;
    }

    this.canReOrder = !this.canReOrder;

    if (this.reorderGroup) {
      this.reorderGroup.disabled =
        !this.canReOrder;
    }
  }

  /**
   * Reordenamiento.
   */
  doReorder(event: any): void {

    /**
     * No permitir reordenar con búsqueda.
     */
    if (this.search.trim()) {

      event.detail.complete();

      return;
    }

    const from = event.detail.from;

    const to = event.detail.to;

    /**
     * Movemos directamente dentro
     * de displayedAdvertisings.
     */
    const item =
      this.displayedAdvertisings.splice(
        from,
        1
      )[0];

    this.displayedAdvertisings.splice(
      to,
      0,
      item
    );

    /**
     * Actualizamos order localmente.
     */
    this.displayedAdvertisings.forEach(
      (advertising, index) => {

        advertising.order = index;

      }
    );

    /**
     * También actualizamos el array principal.
     */
    const statusItems =
      this.advertisings.filter(
        advertising =>
          advertising._status === this.selectedStatus
      );

    statusItems.forEach((item) => {

      const index =
        this.displayedAdvertisings.findIndex(
          advertising =>
            advertising.id === item.id
        );

      if (index !== -1) {
        item.order = index;
      }

    });

    /**
     * Reordenar visualmente.
     */
    this.displayedAdvertisings.sort(
      (a, b) => a.order - b.order
    );

    event.detail.complete();
  }

  /**
   * Guarda el orden actual.
   *
   * Funciona tanto para activas como inactivas.
   */
  async saveOrder(): Promise<void> {

    if (this.search.trim()) {

      await this.showToast(
        'Limpia la búsqueda antes de guardar el orden',
        'warning'
      );

      return;
    }

    const loading =
      await this.loadingCtrl.create({
        message: 'Guardando orden...'
      });

    await loading.present();

    try {

      const collection =
        this.selectedStatus === 'active'
          ? this.ACTIVE_COLLECTION
          : this.INACTIVE_COLLECTION;

      const batch =
        this.firestore.firestore.batch();

      /**
       * Guardamos exactamente el orden
       * que aparece en pantalla.
       */
      this.displayedAdvertisings.forEach(
        (advertising, index) => {

          const ref =
            this.firestore
              .collection(collection)
              .doc(advertising.id)
              .ref;

          batch.update(ref, {
            order: index
          });

        }
      );

      await batch.commit();

      /**
       * Actualizar valores locales.
       */
      this.displayedAdvertisings.forEach(
        (advertising, index) => {

          advertising.order = index;

          const original =
            this.advertisings.find(
              item =>
                item.id === advertising.id
            );

          if (original) {
            original.order = index;
          }

        }
      );

      this.canReOrder = false;

      if (this.reorderGroup) {
        this.reorderGroup.disabled = true;
      }

      await loading.dismiss();

      await this.showToast(
        'Orden guardado correctamente',
        'success'
      );

    } catch (error) {

      log.error(error);

      await loading.dismiss();

      await this.showToast(
        'No se pudo guardar el orden',
        'danger'
      );
    }
  }

  /**
   * Crear / editar.
   */
  async openAdvertising(
    advertising: any = null,
    accion: 'Crear' | 'Editar' = 'Crear'
  ): Promise<void> {

    const modal =
      await this.modalC.create({

        component: NewAdvertisingComponent,

        componentProps: {
          advertising,
          accion
        },

        cssClass: 'modal-class'

      });

    modal.onDidDismiss()
      .then(() => {

        this.initializeApp();

      });

    await modal.present();
  }

  /**
   * Desactivar.
   */
  async deactivateAdvertising(
    advertising: any
  ): Promise<void> {

    const alert =
      await this.alertC.create({

        header: 'Desactivar publicidad',

        message:
          'La publicidad dejará de mostrarse en la aplicación. ¿Deseas continuar?',

        buttons: [

          {
            text: 'Cancelar',
            role: 'cancel'
          },

          {
            text: 'Desactivar',

            handler: async () => {

              try {

                const loading =
                  await this.loadingCtrl.create({
                    message: 'Desactivando...'
                  });

                await loading.present();

                const id =
                  advertising.id;

                const activeRef =
                  this.firestore
                    .collection(
                      this.ACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                const inactiveRef =
                  this.firestore
                    .collection(
                      this.INACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                /**
                 * Copiamos absolutamente todos
                 * los datos originales.
                 */
                const data = {
                  ...advertising
                };

                /**
                 * Campo interno del administrador.
                 */
                delete data._status;

                const batch =
                  this.firestore.firestore.batch();

                /**
                 * Guardar en respaldo.
                 */
                batch.set(
                  inactiveRef,
                  data
                );

                /**
                 * Quitar de la colección
                 * utilizada por la aplicación.
                 */
                batch.delete(activeRef);

                await batch.commit();

                await loading.dismiss();

                await this.showToast(
                  'Publicidad desactivada correctamente',
                  'success'
                );

                await this.initializeApp();

              } catch (error) {

                log.error(error);

                await this.showToast(
                  'No se pudo desactivar la publicidad',
                  'danger'
                );
              }
            }
          }

        ]

      });

    await alert.present();
  }

  /**
   * Activar.
   */
  async activateAdvertising(
    advertising: any
  ): Promise<void> {

    const alert =
      await this.alertC.create({

        header: 'Activar publicidad',

        message:
          'La publicidad volverá a mostrarse en la aplicación. ¿Deseas continuar?',

        buttons: [

          {
            text: 'Cancelar',
            role: 'cancel'
          },

          {
            text: 'Activar',

            handler: async () => {

              try {

                const loading =
                  await this.loadingCtrl.create({
                    message: 'Activando...'
                  });

                await loading.present();

                const id =
                  advertising.id;

                const inactiveRef =
                  this.firestore
                    .collection(
                      this.INACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                const activeRef =
                  this.firestore
                    .collection(
                      this.ACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                const data = {
                  ...advertising
                };

                delete data._status;

                const batch =
                  this.firestore.firestore.batch();

                /**
                 * Regresar a colección activa.
                 */
                batch.set(
                  activeRef,
                  data
                );

                /**
                 * Eliminar respaldo.
                 */
                batch.delete(
                  inactiveRef
                );

                await batch.commit();

                await loading.dismiss();

                await this.showToast(
                  'Publicidad activada correctamente',
                  'success'
                );

                await this.initializeApp();

              } catch (error) {

                log.error(error);

                await this.showToast(
                  'No se pudo activar la publicidad',
                  'danger'
                );
              }
            }
          }

        ]

      });

    await alert.present();
  }

  /**
   * Eliminar definitivamente.
   */
  async deleteAdvertising(
    id: string
  ): Promise<void> {

    const alert =
      await this.alertC.create({

        header: 'Eliminar publicidad',

        message:
          'Esta acción eliminará definitivamente la publicidad. ¿Deseas continuar?',

        buttons: [

          {
            text: 'Cancelar',
            role: 'cancel'
          },

          {
            text: 'Eliminar',

            role: 'destructive',

            handler: async () => {

              try {

                const loading =
                  await this.loadingCtrl.create({
                    message: 'Eliminando...'
                  });

                await loading.present();

                /**
                 * Storage.
                 */
                try {

                  await this.aStorage
                    .ref(`advertising/${id}`)
                    .delete()
                    .toPromise();

                } catch (storageError) {

                  log.error(storageError);

                }

                const activeRef =
                  this.firestore
                    .collection(
                      this.ACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                const inactiveRef =
                  this.firestore
                    .collection(
                      this.INACTIVE_COLLECTION
                    )
                    .doc(id)
                    .ref;

                const batch =
                  this.firestore.firestore.batch();

                batch.delete(activeRef);

                batch.delete(inactiveRef);

                await batch.commit();

                await loading.dismiss();

                await this.showToast(
                  'Publicidad eliminada',
                  'success'
                );

                await this.initializeApp();

              } catch (error) {

                log.error(error);

                await this.showToast(
                  'No se pudo eliminar la publicidad',
                  'danger'
                );
              }
            }
          }

        ]

      });

    await alert.present();
  }

  /**
   * Toast genérico.
   */
  private async showToast(
    message: string,
    color:
      | 'success'
      | 'danger'
      | 'warning'
      | 'primary' = 'primary'
  ): Promise<void> {

    const toast =
      await this.toastC.create({

        message,

        duration: 1800,

        position: 'bottom',

        color

      });

    await toast.present();
  }

  /**
   * Cantidad de activas.
   */
  get activeCount(): number {

    return this.advertisings.filter(
      advertising =>
        advertising._status === 'active'
    ).length;
  }

  /**
   * Cantidad de inactivas.
   */
  get inactiveCount(): number {

    return this.advertisings.filter(
      advertising =>
        advertising._status === 'inactive'
    ).length;
  }
}